// 封装axios
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { jumpLogin } from '@/utils';
import Cookies from 'js-cookie';
import { showMessage } from '@/utils/message';

export const createAxiosByInterceptors = (
	config?: AxiosRequestConfig
): AxiosInstance => {
	const instance = axios.create({
		timeout: 30 * 1000, //超时配置,15s
		withCredentials: true, //跨域携带cookie
		...config, // 自定义配置覆盖基本配置
	});

	// 添加请求拦截器,请求之前统一处理config信息
	instance.interceptors.request.use(
		function (config: any) {
			// 在发送请求之前做些什么
			config.headers.Authorization = Cookies.get('token');
			return config;
		},
		function (error: any) {
			// 对请求错误做些什么
			return Promise.reject(error);
		}
	);

	// 添加响应拦截器,统一处理某些响应
	instance.interceptors.response.use(
		function (response: any) {
			// 对响应数据做点什么
			// console.log('response拦截:', response);

			// 处理http状态码
			const { status } = response;
			switch (status) {
				case 200:
				case 201:
					break;
				case 401:
					jumpLogin();
					break;
				case 403:
					console.log('登录过期，请重新登录');
					jumpLogin();
					break;
				case 404:
					console.log('请求资源不存在');
					break;
				default:
					console.log('请求失败');
					break;
			}

			// 接口返回的数据
			const { code, httpCode, message } = response.data;

			if (httpCode === 401) {
				jumpLogin();
				return Promise.reject(response.data);
			} else if (httpCode === 403) {
				showMessage.warning(`无权限：${message}`);
				jumpLogin();
				return Promise.reject(response.data);
			}

			if (code === 0) {
				return response;
			} else {
				return Promise.reject(response.data);
			}
		},
		function (error: any) {
			// 对响应错误做点什么
			console.log('error-response:', error.response);
			console.log('error-config:', error.config);
			console.log('error-request:', error.request);
			if (error.response) {
				if (error.response.status === 401 || error.response.status === 403) {
					jumpLogin();
					showMessage.warning(error.response.data.message);
					console.log(error.response.data.message);
				}
			}
			console.log(error?.response?.data?.message || '服务端异常');
			return Promise.reject(error);
		}
	);

	return instance;
};

const axiosInstance = createAxiosByInterceptors({
	// baseURL: `${baseURL}:${basePort}`,
	timeout: 15000,
});

export const get = (url: string, config?: {}) => axiosInstance.get(url, config);

export const post = async (url: string, data?: {}, config?: {}) => {
	// addLoading();
	const res = await axiosInstance.post(url, data, config);
	// cancelLoading();
	if (res.data.code === 0) {
		return res;
	} else {
		return Promise.reject(res.data);
	}
};

export const put = (url: string, data: {}, config?: {}) =>
	axiosInstance.put(url, data, config);

export const del = (url: string, config?: {}) =>
	axiosInstance.delete(url, config);

// 处理流式数据的类型定义
interface ChunkDelta {
	content: string;
	role: string;
}

interface ChunkChoice {
	delta: ChunkDelta;
	index: number;
	logprobs: null;
	finish_reason: string | null;
}

interface StreamChunk {
	choices: ChunkChoice[];
	object: string;
	usage: null;
	created: number;
	system_fingerprint: null;
	model: string;
	id: string;
}

// 处理流式数据的配置
interface StreamOptions {
	onMessage?: (content: string) => void;
	onFinish?: () => void;
	onError?: (error: Error) => void;
}

// 封装流式请求方法
export const postStream = async (
	url: string,
	data?: any,
	options: StreamOptions = {}
): Promise<void> => {
	const { onMessage, onFinish, onError } = options;
	
	try {
		const response = await fetch(`${url}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Transfer-Encoding': 'chunked',
				Authorization: Cookies.get('token') || '',
			},
			body: JSON.stringify(data),
		});

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		
		const reader = response.body?.getReader();
		if (!reader) throw new Error('No readable stream available');

		const decoder = new TextDecoder();
		let buffer = '';

		while (true) {
			const { done, value } = await reader.read();
			
			if (done) {
				onFinish?.();
				break;
			}

			// 将新的数据块添加到缓冲区
			buffer += decoder.decode(value, { stream: true });
			
			// 按行分割并处理每个完整的JSON对象
			const lines = buffer.split('\n');
			buffer = lines.pop() || ''; // 保留最后一个不完整的行

			for (const line of lines) {
				if (line.trim()) {
					try {
						const chunk: StreamChunk = JSON.parse(line);
						
						// 处理内容
						const content = chunk.choices[0]?.delta?.content || '';
						if (content) {
							onMessage?.(content);
						}

						// 检查是否完成
						if (chunk.choices[0]?.finish_reason === 'stop') {
							onFinish?.();
						}
					} catch (error) {
						console.warn('Error parsing chunk:', error);
					}
				}
			}
		}
	} catch (error) {
		onError?.(error as Error);
	}
};
